import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = 'gop_admin_jwt';

// --------------------------------------------------------------------------- //
// Auth context
// --------------------------------------------------------------------------- //
const AdminAuthCtx = createContext({ jwt: null, setJwt: () => {}, logout: () => {} });

const useAdminAuth = () => useContext(AdminAuthCtx);

const authedAxios = () => {
  const jwt = localStorage.getItem(LS_KEY);
  return axios.create({
    baseURL: API,
    headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
    withCredentials: true,
  });
};

export const AdminAuthProvider = ({ children }) => {
  const [jwt, setJwt] = useState(() => localStorage.getItem(LS_KEY));
  const nav = useNavigate();
  const logout = async () => {
    try { await authedAxios().post('/admin/logout'); } catch (e) { /* fire-and-forget */ }
    localStorage.removeItem(LS_KEY);
    setJwt(null);
    nav('/admin');
  };
  useEffect(() => {
    if (jwt) localStorage.setItem(LS_KEY, jwt);
  }, [jwt]);
  return (
    <AdminAuthCtx.Provider value={{ jwt, setJwt, logout }}>{children}</AdminAuthCtx.Provider>
  );
};

// --------------------------------------------------------------------------- //
// Login page
// --------------------------------------------------------------------------- //
export const AdminLogin = () => {
  const { jwt, setJwt } = useAdminAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { token }, { withCredentials: true });
      localStorage.setItem(LS_KEY, data.jwt);
      setJwt(data.jwt);
      nav('/admin/dashboard');
    } catch (e) {
      setError(e.response?.status === 401 ? 'Wrong admin token.' : 'Login failed.');
    } finally {
      setLoading(false);
    }
  };
  if (jwt) return <Navigate to="/admin/dashboard" replace />;
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Genius One Plus — Admin</CardTitle>
          <CardDescription>Sign in with your admin token to access the panel.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <Input
              type="password"
              placeholder="Admin token"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              autoFocus
              data-testid="admin-token-input"
              required
            />
            {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-submit">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Layout with dark sidebar
// --------------------------------------------------------------------------- //
const NAV = [
  { to: '/admin/dashboard', label: 'Overview', icon: '📊' },
  { to: '/admin/funnel', label: 'Funnel', icon: '📈' },
  { to: '/admin/payments', label: 'Payments', icon: '💳' },
  { to: '/admin/students', label: 'Students', icon: '🧒' },
  { to: '/admin/reminders', label: 'Reminders', icon: '📣' },
];

export const AdminLayout = () => {
  const { jwt, logout } = useAdminAuth();
  const [open, setOpen] = useState(false);
  if (!jwt) return <Navigate to="/admin" replace />;
  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="admin-layout">
      {/* Sidebar (desktop + drawer) */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-slate-900 text-slate-100 flex-col ${open ? 'flex' : 'hidden lg:flex'}`}>
        <div className="px-5 py-6 border-b border-slate-800">
          <p className="text-xs text-slate-400 uppercase tracking-wider">Genius One Plus</p>
          <p className="text-lg font-bold">Admin Console</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60'
                }`
              }
            >
              <span>{n.icon}</span><span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <Button variant="ghost" onClick={logout} className="w-full justify-start text-slate-300 hover:text-white" data-testid="admin-logout-btn">
            <span className="mr-2">🚪</span> Logout
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 bg-slate-900 text-slate-100 px-4 py-3 flex items-center justify-between z-30">
        <button onClick={() => setOpen(!open)} data-testid="admin-sidebar-toggle">☰</button>
        <p className="font-semibold">Admin</p>
        <Link to="/" className="text-slate-400 text-sm">Site →</Link>
      </div>

      {/* Main */}
      <main className="flex-1 lg:ml-0 mt-14 lg:mt-0 p-6"><Outlet /></main>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Overview page
// --------------------------------------------------------------------------- //
const StatCard = ({ label, value, hint, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2"><CardDescription>{label}</CardDescription></CardHeader>
    <CardContent>
      <div className="text-3xl font-bold">{value}</div>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

export const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    authedAxios().get('/admin/stats').then(r => setData(r.data)).catch(e => setErr(e.message));
  }, []);
  if (err) return <p className="text-red-600">{err}</p>;
  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="admin-dashboard-page">
      <h1 className="text-2xl font-bold">Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard testId="stat-total-registrations" label="Total registrations" value={data.total_registrations} />
        <StatCard testId="stat-active-students" label="Active students" value={data.active_students} />
        <StatCard testId="stat-pending-payments" label="Pending payments" value={data.pending_payments} hint="awaiting your review" />
        <StatCard testId="stat-pending-reminders" label="Pending reminders" value={data.pending_reminders || 0} hint="Day-3 nudge queue" />
        <StatCard testId="stat-free-october" label="Free October" value={data.free_october_signups} />
      </div>

      <div className="mt-4">
        <FunnelWidget />
      </div>
      <Card data-testid="revenue-card">
        <CardHeader><CardTitle>Revenue this month</CardTitle></CardHeader>
        <CardContent>
          <div className="text-3xl font-bold">LKR {data.revenue_this_month_lkr.toLocaleString()}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {data.revenue_this_month_count} approved payment{data.revenue_this_month_count === 1 ? '' : 's'}
          </p>
        </CardContent>
      </Card>
      <Card data-testid="activity-feed">
        <CardHeader><CardTitle>Recent activity</CardTitle></CardHeader>
        <CardContent>
          {data.activity.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <ul className="divide-y">
              {data.activity.map((a, i) => (
                <li key={i} className="py-2 text-sm flex justify-between gap-4">
                  <span>{a.text}</span>
                  <span className="text-muted-foreground text-xs">{(a.at || '').slice(0, 16).replace('T', ' ')}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Payments page
// --------------------------------------------------------------------------- //
export const AdminPayments = () => {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [modal, setModal] = useState(null); // { action: 'approve'|'reject', row, reason? }

  const load = () => {
    setLoading(true);
    authedAxios().get(`/admin/payments?status=${status}&limit=100`)
      .then(r => { setRows(r.data.rows || []); setCount(r.data.count || 0); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [status]);

  const runAction = async () => {
    if (!modal) return;
    try {
      if (modal.action === 'approve') {
        await authedAxios().post('/admin/payment/approve', { payment_id: modal.row.id });
      } else if (modal.action === 'reject') {
        await authedAxios().post('/admin/payment/reject', {
          payment_id: modal.row.id,
          reason: modal.reason || 'no reason given',
        });
      }
      setModal(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Action failed');
    }
  };

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  return (
    <div className="space-y-4" data-testid="admin-payments-page">
      <h1 className="text-2xl font-bold">Payments</h1>
      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`filter-${s}-btn`}
            className={`px-3 py-1.5 rounded-md text-sm border ${
              status === s ? 'bg-slate-900 text-white' : 'bg-white'
            }`}
          >
            {s}
          </button>
        ))}
        <span className="text-sm text-muted-foreground ml-auto self-center">{count} total</span>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2">Parent</th>
                <th className="px-3 py-2">Student</th>
                <th className="px-3 py-2">Plan</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2">Submitted</th>
                <th className="px-3 py-2">Receipt</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-3 py-4 text-muted-foreground">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-4 text-muted-foreground">No payments.</td></tr>
              ) : rows.map(r => {
                const receiptUrl = r.receipt_path ? `${backendBase}${r.receipt_path}` : null;
                return (
                  <tr key={r.id} className="border-t" data-testid={`payment-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.parent_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{r.parent_whatsapp}</div>
                    </td>
                    <td className="px-3 py-2">{r.student_name || '—'}</td>
                    <td className="px-3 py-2 uppercase">{r.plan}</td>
                    <td className="px-3 py-2 text-right">LKR {r.amount_lkr?.toLocaleString?.() || r.amount_lkr}</td>
                    <td className="px-3 py-2 text-xs">{(r.submitted_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2">
                      {receiptUrl ? (
                        <img src={receiptUrl} alt="receipt" className="w-12 h-12 object-cover rounded border cursor-pointer"
                             onClick={() => setLightbox(receiptUrl)}
                             data-testid={`receipt-thumb-${r.id}`}
                             onError={(e) => e.currentTarget.style.opacity = '0.3'} />
                      ) : <span className="text-muted-foreground text-xs">n/a</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant={r.status === 'approved' ? 'default' : r.status === 'rejected' ? 'destructive' : 'secondary'}>
                        {r.status.replace('pending_admin_review', 'pending')}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {r.status === 'pending_admin_review' && (
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => setModal({ action: 'approve', row: r })}
                                  data-testid={`approve-${r.id}`}>Approve</Button>
                          <Button size="sm" variant="destructive" onClick={() => setModal({ action: 'reject', row: r, reason: '' })}
                                  data-testid={`reject-${r.id}`}>Reject</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {lightbox && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-8"
             onClick={() => setLightbox(null)} data-testid="lightbox">
          <img src={lightbox} alt="receipt full" className="max-w-full max-h-full rounded shadow-2xl" />
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md" data-testid="payment-action-modal">
            <CardHeader>
              <CardTitle>{modal.action === 'approve' ? 'Approve payment?' : 'Reject payment?'}</CardTitle>
              <CardDescription>{modal.row.parent_name} · LKR {modal.row.amount_lkr}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {modal.action === 'reject' && (
                <div>
                  <label className="text-sm font-medium block mb-1">Reason (required)</label>
                  <Input value={modal.reason || ''}
                         onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                         placeholder="e.g. blurry image, wrong amount…"
                         data-testid="reject-reason-input" />
                </div>
              )}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setModal(null)}>Cancel</Button>
                <Button
                  variant={modal.action === 'reject' ? 'destructive' : 'default'}
                  className="flex-1"
                  disabled={modal.action === 'reject' && (!modal.reason || modal.reason.length < 3)}
                  onClick={runAction}
                  data-testid="confirm-action-btn">
                  Confirm
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Students page + detail drawer
// --------------------------------------------------------------------------- //
const SL_DISTRICTS = [
  'Ampara','Anuradhapura','Badulla','Batticaloa','Colombo','Galle','Gampaha',
  'Hambantota','Jaffna','Kalutara','Kandy','Kegalle','Kilinochchi','Kurunegala',
  'Mannar','Matale','Matara','Monaragala','Mullaitivu','Nuwara Eliya',
  'Polonnaruwa','Puttalam','Ratnapura','Trincomalee','Vavuniya'
];
const LANGS = ['en','si','ta','si-en','ta-en'];
const STATUSES = ['active','pending_payment','active_free_october','lapsed'];

const WA_TEMPLATES = {
  en: (n, pu) => `Hi ${n}, this is Genius One Plus. Your access ${pu ? `expires on ${pu.slice(0,10)}` : 'expires soon'}. Please transfer LKR 750 to BOC 005000189991653 — send receipt to +94763845556. Thanks!`,
  si: (n, pu) => `ආයුබෝවන් ${n}, මේ Genius One Plus. ඔබේ ප්‍රවේශය ${pu ? `${pu.slice(0,10)} දින අවසන් වේ` : 'ඉක්මනින් අවසන් වේ'}. කරුණාකර BOC 005000189991653 ට LKR 750 ක් යවා රිසිට් +94763845556 ට WhatsApp කරන්න. ස්තුතියි!`,
  ta: (n, pu) => `வணக்கம் ${n}, இது Genius One Plus. உங்கள் அணுகல் ${pu ? `${pu.slice(0,10)} அன்று முடிகிறது` : 'விரைவில் முடிகிறது'}. BOC 005000189991653 க்கு LKR 750 அனுப்பி ரசீதை +94763845556 க்கு WhatsApp செய்யவும். நன்றி!`,
};

export const AdminStudents = () => {
  const [filters, setFilters] = useState({ grade: '', district: '', status: '', language: '', search: '' });
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [drawer, setDrawer] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, days? }

  const load = () => {
    const qs = new URLSearchParams();
    if (filters.grade) qs.set('grade', filters.grade);
    if (filters.district) qs.set('district', filters.district);
    if (filters.status) qs.set('status', filters.status);
    if (filters.language) qs.set('language', filters.language);
    if (filters.search) qs.set('search', filters.search);
    authedAxios().get(`/admin/students?${qs.toString()}&limit=200`)
      .then(r => { setRows(r.data.rows || []); setCount(r.data.count || 0); });
  };
  useEffect(load, [filters]);

  const openDetail = async (reg_id) => {
    const { data } = await authedAxios().get(`/admin/students/${reg_id}/detail`);
    setDrawer(data);
  };

  const runConfirm = async () => {
    if (!drawer || !confirm) return;
    const reg_id = drawer.registration.id;
    try {
      if (confirm.action === 'extend') {
        await authedAxios().post(`/admin/students/${reg_id}/extend`, { days: confirm.days });
      } else if (confirm.action === 'lapse') {
        await authedAxios().post(`/admin/students/${reg_id}/mark-lapsed`);
      }
      setConfirm(null);
      openDetail(reg_id);
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const copyWA = () => {
    if (!drawer) return;
    const lang = (drawer.registration.language || 'en').split('-')[0];
    const tpl = WA_TEMPLATES[lang] || WA_TEMPLATES.en;
    const msg = tpl(drawer.registration.parent_name, drawer.registration.paid_until);
    navigator.clipboard.writeText(msg);
    alert('Message copied to clipboard.');
  };

  const downloadCsv = async () => {
    const qs = new URLSearchParams();
    ['grade','district','status','language','search'].forEach(k => filters[k] && qs.set(k, filters[k]));
    const jwt = localStorage.getItem(LS_KEY);
    const res = await fetch(`${API}/admin/students/export.csv?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${jwt}` }, credentials: 'include',
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'students.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-students-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Students</h1>
        <Button variant="outline" onClick={downloadCsv} data-testid="csv-export-btn">Export CSV</Button>
      </div>
      <Card><CardContent className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        <select value={filters.grade} onChange={(e) => setFilters({ ...filters, grade: e.target.value })}
                data-testid="filter-grade" className="h-10 rounded-md border px-3 text-sm">
          <option value="">All grades</option><option value="5">Grade 5</option><option value="6">Grade 6</option>
        </select>
        <select value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })}
                data-testid="filter-district" className="h-10 rounded-md border px-3 text-sm">
          <option value="">All districts</option>{SL_DISTRICTS.map(d => <option key={d}>{d}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                data-testid="filter-status" className="h-10 rounded-md border px-3 text-sm">
          <option value="">All statuses</option>{STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        <select value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })}
                data-testid="filter-language" className="h-10 rounded-md border px-3 text-sm">
          <option value="">All languages</option>{LANGS.map(l => <option key={l}>{l}</option>)}
        </select>
        <Input placeholder="Search name / phone / email"
               value={filters.search}
               onChange={(e) => setFilters({ ...filters, search: e.target.value })}
               data-testid="filter-search" />
      </CardContent></Card>

      <p className="text-sm text-muted-foreground">{count} students</p>

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-left">
            <tr>
              <th className="px-3 py-2">Parent</th>
              <th className="px-3 py-2">Student</th>
              <th className="px-3 py-2">Grade</th>
              <th className="px-3 py-2">District</th>
              <th className="px-3 py-2">Lang</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Paid until</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} onClick={() => openDetail(r.id)} className="border-t cursor-pointer hover:bg-slate-50"
                  data-testid={`student-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.parent_name}</div>
                  <div className="text-xs text-muted-foreground">{r.parent_whatsapp}</div>
                </td>
                <td className="px-3 py-2">{r.student_name}</td>
                <td className="px-3 py-2">{r.grade}</td>
                <td className="px-3 py-2">{r.district}</td>
                <td className="px-3 py-2 uppercase text-xs">{r.language}</td>
                <td className="px-3 py-2">
                  <Badge variant={r.status === 'active' || r.status === 'active_free_october' ? 'default' : 'secondary'}>
                    {r.status}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-xs">{(r.paid_until || '').slice(0, 10) || '—'}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-muted-foreground">No students match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>

      {drawer && (
        <div className="fixed inset-0 bg-black/40 z-40 flex justify-end" onClick={() => setDrawer(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-6 space-y-4"
               onClick={(e) => e.stopPropagation()} data-testid="student-detail-drawer">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">{drawer.registration.parent_name}</h2>
                <p className="text-sm text-muted-foreground">
                  {drawer.registration.student_name} · Grade {drawer.registration.grade}
                </p>
              </div>
              <button onClick={() => setDrawer(null)} className="text-2xl leading-none">×</button>
            </div>
            <Card><CardContent className="p-4 text-sm space-y-1">
              <div><strong>WhatsApp:</strong> {drawer.registration.parent_whatsapp}</div>
              <div><strong>District:</strong> {drawer.registration.district}</div>
              <div><strong>Language:</strong> {drawer.registration.language}</div>
              <div><strong>Status:</strong> {drawer.registration.status}</div>
              <div><strong>Paid until:</strong> {(drawer.registration.paid_until || '—').slice(0, 10)}</div>
              <div><strong>Quiz attempts:</strong> {drawer.attempts_count}</div>
              <div><strong>Badges:</strong> {drawer.badges.length === 0 ? '—' : drawer.badges.join(', ')}</div>
            </CardContent></Card>

            <div className="grid grid-cols-1 gap-2">
              <Button variant="outline" onClick={() => setConfirm({ action: 'extend', days: 30 })}
                      data-testid="extend-30-btn">Extend paid_until by 30 days</Button>
              <Button variant="outline" onClick={copyWA} data-testid="copy-wa-btn">
                Copy WhatsApp reminder (lang: {(drawer.registration.language || 'en').split('-')[0]})
              </Button>
              <Button variant="destructive" onClick={() => setConfirm({ action: 'lapse' })}
                      data-testid="mark-lapsed-btn">Mark as lapsed</Button>
              <Button variant="outline" asChild data-testid="issue-id-card-btn">
                <a href={`/id-card/${drawer.registration.id}`} target="_blank" rel="noreferrer">
                  Issue / view ID card →
                </a>
              </Button>
            </div>

            <Card><CardHeader><CardTitle className="text-base">Timeline</CardTitle></CardHeader>
              <CardContent>
                {drawer.timeline.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No events.</p>
                ) : (
                  <ul className="text-sm space-y-1">
                    {drawer.timeline.map((t, i) => (
                      <li key={i} className="flex justify-between gap-3">
                        <span>{t.text}</span>
                        <span className="text-xs text-muted-foreground">{(t.at || '').slice(0, 16).replace('T', ' ')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {confirm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <Card className="w-full max-w-sm">
                  <CardHeader>
                    <CardTitle>Confirm</CardTitle>
                    <CardDescription>
                      {confirm.action === 'extend'
                        ? `Extend access by ${confirm.days} days?`
                        : 'Mark this student as lapsed?'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => setConfirm(null)}>Cancel</Button>
                    <Button className="flex-1" onClick={runConfirm}
                            variant={confirm.action === 'lapse' ? 'destructive' : 'default'}
                            data-testid="confirm-btn">Confirm</Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Nested routes exported to App.js
// --------------------------------------------------------------------------- //
// Reminders queue (Sprint 9)
// --------------------------------------------------------------------------- //
export const AdminReminders = () => {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, sent: 0, skipped: 0 });
  const [status, setStatus] = useState('pending');
  const [err, setErr] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const load = React.useCallback(() => {
    authedAxios().get(`/admin/reminders?status=${status}&limit=200`)
      .then(r => { setRows(r.data.reminders || []); setCounts(r.data.counts || {}); })
      .catch(e => setErr(e.message));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const markSent = async (id) => {
    await authedAxios().post(`/admin/reminders/${id}/mark-sent`, {});
    load();
  };
  const skip = async (id) => {
    const reason = window.prompt('Skip reason?', 'invalid number');
    if (!reason) return;
    await authedAxios().post(`/admin/reminders/${id}/skip`, { reason });
    load();
  };
  const copy = (row) => {
    navigator.clipboard.writeText(row.message).then(() => {
      setCopiedId(row.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/reminders/export.csv?status=${status}`, {
      credentials: 'include',
    });
    if (!res.ok) { alert('Export failed'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reminders_${status}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-reminders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Day-3 reminder queue</h1>
          <p className="text-sm text-muted-foreground">
            Parents who registered 3 days ago and still haven&rsquo;t started their first quiz.
            Send via WhatsApp Business, then <em>Mark as sent</em>.
          </p>
        </div>
        <div className="flex gap-2">
          {['pending', 'sent', 'skipped', 'all'].map(s => (
            <button key={s}
                    onClick={() => setStatus(s)}
                    data-testid={`reminders-tab-${s}`}
                    className={`px-3 py-1.5 rounded-md text-sm border ${
                      status === s ? 'bg-primary text-primary-foreground' : 'bg-background'
                    }`}>
              {s} {counts[s] != null ? `(${counts[s]})` : ''}
            </button>
          ))}
          <Button onClick={exportCsv} variant="outline" data-testid="reminders-export-csv">
            📄 Export CSV
          </Button>
        </div>
      </div>

      {err && <p className="text-red-600 text-sm">{err}</p>}

      <div className="overflow-x-auto rounded-lg border bg-white">
        <table className="w-full text-sm" data-testid="reminders-table">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Parent</th>
              <th className="text-left px-3 py-2">WhatsApp</th>
              <th className="text-left px-3 py-2">Lang</th>
              <th className="text-left px-3 py-2">Message</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Queued</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">
                No reminders {status !== 'all' && `with status “${status}”`}.
              </td></tr>
            ) : rows.map(r => (
              <tr key={r.id} data-testid={`reminder-row-${r.id}`}>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.parent_name}</div>
                  <div className="text-xs text-muted-foreground">{r.student_name}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.parent_whatsapp_masked}</td>
                <td className="px-3 py-2 text-xs uppercase">{r.language}</td>
                <td className="px-3 py-2 max-w-md">
                  <div className="text-xs line-clamp-2">{r.message}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    r.status === 'pending' ? 'bg-amber-100 text-amber-900' :
                    r.status === 'sent' ? 'bg-emerald-100 text-emerald-900' :
                    'bg-slate-100 text-slate-700'
                  }`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {(r.created_at || '').slice(0, 16).replace('T', ' ')}
                </td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => copy(r)} className="text-xs underline"
                          data-testid={`reminder-copy-${r.id}`}>
                    {copiedId === r.id ? '✓ Copied' : '📋 Copy'}
                  </button>
                  {r.status === 'pending' && (
                    <>
                      <button onClick={() => markSent(r.id)} className="text-xs underline text-emerald-700"
                              data-testid={`reminder-mark-sent-${r.id}`}>
                        Mark sent
                      </button>
                      <button onClick={() => skip(r.id)} className="text-xs underline text-slate-500"
                              data-testid={`reminder-skip-${r.id}`}>
                        Skip
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------- //
// Marketing funnel (Sprint 10)
// --------------------------------------------------------------------------- //
const FUNNEL_STAGES = [
  { key: 'total_registered',     label: 'Registered',           color: 'bg-slate-500' },
  { key: 'reminder_queued',      label: 'Reminder queued',       color: 'bg-amber-500' },
  { key: 'reminder_sent',        label: 'Reminder sent',         color: 'bg-amber-600' },
  { key: 'first_quiz_started',   label: 'First quiz started',   color: 'bg-sky-500' },
  { key: 'first_quiz_completed', label: 'First quiz completed', color: 'bg-sky-600' },
  { key: 'first_quiz_perfect',   label: 'First quiz perfect',   color: 'bg-indigo-600' },
  { key: 'payment_submitted',    label: 'Payment submitted',    color: 'bg-emerald-500' },
  { key: 'payment_approved',     label: 'Payment approved',     color: 'bg-emerald-700' },
];

export const AdminFunnel = () => {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [err, setErr] = useState(null);

  const load = React.useCallback(() => {
    setErr(null);
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const q = params.toString();
    authedAxios().get(`/admin/funnel${q ? `?${q}` : ''}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.message));
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-red-600 text-sm">{err}</p>;
  if (!data) return <p className="text-muted-foreground text-sm">Loading funnel…</p>;

  const top = Math.max(1, data.total_registered || 1);
  const barWidth = (n) => `${Math.max(4, (n / top) * 100).toFixed(1)}%`;

  return (
    <div className="space-y-6" data-testid="admin-funnel-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Marketing funnel</h1>
          <p className="text-sm text-muted-foreground">
            Registered → nudged → quizzed → paid. Widen the date window to see legacy cohorts.
          </p>
        </div>
        <div className="flex items-end gap-2 text-sm">
          <label className="flex flex-col">
            <span className="text-xs uppercase text-slate-500">From</span>
            <Input type="date" value={from} onChange={e => setFrom(e.target.value)}
                   data-testid="funnel-from" />
          </label>
          <label className="flex flex-col">
            <span className="text-xs uppercase text-slate-500">To</span>
            <Input type="date" value={to} onChange={e => setTo(e.target.value)}
                   data-testid="funnel-to" />
          </label>
          <Button onClick={load} data-testid="funnel-apply">Apply</Button>
        </div>
      </div>

      {/* Vertical stacked bars */}
      <div className="space-y-2" data-testid="funnel-bars">
        {FUNNEL_STAGES.map((s, i) => {
          const n = data[s.key] || 0;
          const prev = i === 0 ? n : (data[FUNNEL_STAGES[i - 1].key] || 0);
          const drop = prev > 0 ? Math.round((1 - n / prev) * 100) : 0;
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`funnel-stage-${s.key}`}>
              <div className="w-44 text-sm text-slate-700 font-medium">{s.label}</div>
              <div className="flex-1 bg-slate-100 rounded overflow-hidden">
                <div className={`${s.color} h-6 rounded transition-all`}
                     style={{ width: barWidth(n) }} />
              </div>
              <div className="w-16 text-right text-sm font-mono">{n}</div>
              <div className={`w-24 text-right text-xs ${
                i === 0 ? 'text-slate-400' : 'text-slate-500'
              }`}>
                {i === 0 ? '—' : `↓ ${drop}% drop`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Conversion cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(data.conversion_rates).map(([k, v]) => (
          <div key={k} className="p-3 rounded-lg border bg-white"
               data-testid={`conv-${k}`}>
            <div className="text-xs text-muted-foreground">
              {k.replaceAll('_', ' ')}
            </div>
            <div className="text-2xl font-bold">{Math.round(v * 100)}%</div>
          </div>
        ))}
      </div>

      {/* By-language + by-district */}
      <div className="grid md:grid-cols-2 gap-4">
        <BreakdownTable title="By language" rows={data.by_language || {}} />
        <BreakdownTable title="By district" rows={data.by_district || {}} />
      </div>

      <p className="text-xs text-muted-foreground">
        Range: {data.date_range?.from?.slice(0, 10)} → {data.date_range?.to?.slice(0, 10)}
      </p>
    </div>
  );
};

const BreakdownTable = ({ title, rows }) => (
  <div className="rounded-lg border bg-white overflow-hidden">
    <div className="px-3 py-2 border-b font-medium text-sm bg-slate-50">{title}</div>
    <table className="w-full text-xs">
      <thead className="text-slate-500">
        <tr>
          <th className="text-left px-3 py-2">Key</th>
          <th className="text-right px-3 py-2">Reg</th>
          <th className="text-right px-3 py-2">Quiz</th>
          <th className="text-right px-3 py-2">Paid</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {Object.keys(rows).length === 0
          ? <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No data</td></tr>
          : Object.entries(rows).map(([k, v]) => (
              <tr key={k}>
                <td className="px-3 py-1.5 font-mono">{k}</td>
                <td className="px-3 py-1.5 text-right">{v.registered}</td>
                <td className="px-3 py-1.5 text-right">{v.first_quiz_started}</td>
                <td className="px-3 py-1.5 text-right">{v.payment_approved}</td>
              </tr>
            ))}
      </tbody>
    </table>
  </div>
);

// Compact widget for the main dashboard
export const FunnelWidget = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    authedAxios().get('/admin/funnel').then(r => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const R = data.total_registered || 0;
  const Q = data.first_quiz_started || 0;
  const P = data.payment_approved || 0;
  return (
    <Link to="/admin/funnel"
          className="block p-4 rounded-lg border bg-white hover:shadow"
          data-testid="funnel-widget">
      <div className="text-xs uppercase text-muted-foreground mb-1">Funnel (last 30d)</div>
      <div className="flex items-center gap-3 text-sm">
        <span><strong>{R}</strong> registered</span>
        <span className="text-slate-400">→</span>
        <span><strong>{Q}</strong> started</span>
        <span className="text-slate-400">→</span>
        <span><strong>{P}</strong> paid</span>
      </div>
      <div className="text-xs text-emerald-700 mt-1">
        {R > 0 ? `${Math.round(100 * P / R)}% end-to-end conversion` : '—'}
      </div>
    </Link>
  );
};

// --------------------------------------------------------------------------- //
export const AdminRoutes = () => (
  <AdminAuthProvider>
    <Routes>
      <Route index element={<AdminLogin />} />
      <Route element={<AdminLayout />}>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="funnel" element={<AdminFunnel />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="students" element={<AdminStudents />} />
        <Route path="reminders" element={<AdminReminders />} />
      </Route>
    </Routes>
  </AdminAuthProvider>
);
